import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import * as zVal from '../../../../lib/validations';
import MedicalHealthInformation from './medical-health-information';
import PCPInformation from './pcp-information';
import HealthAssistance from './health-assistance';
import LongTermCare from './long-term-care';
import OtherInsuranceCoverageForm from './other-insurance-coverage-form';
import { healthConditionsCardio, healthConditionsDiabetes } from '../constants';
import Button from '../../../ui/button';
import Text from '../../../ui/text';
import i18n from '../../../../lib/i18n';

const schema = z.object({
  medicareNumber: zVal.textRequired(),
  partACoverageDate: zVal.textRequired(),
  partBCoverageDate: zVal.textRequired(),
  primaryCarePhysician: zVal.textOptionalEmpty(),
  pcpID: zVal.textOptionalEmpty(),
  clinicName: zVal.textOptionalEmpty(),
  PCPPatient: zVal.textOptionalEmpty(),
  PCPPatientOther: zVal.textOptionalEmpty(),
  hasMedicaid: z.preprocess(
    (val) => (val === undefined ? false : val), 
    z.boolean().optional()
  ),
  medicaidNumber: zVal.textOptionalEmpty(),
  hasCardioOrDiabetes: z.preprocess(
    (val) => (val === undefined ? false : val), 
    z.boolean().optional()
  ),
  hasChronicCondition: z.preprocess(
    (val) => (val === undefined ? false : val), 
    z.boolean().optional()
  ),
  healthConditionsCardio: z.array(z.object({ 
    checked: z.boolean().optional(),
  })),
  healthConditionsDiabetes: z.array(z.object({ 
    checked: z.boolean().optional(),
  })),
  specialistInformation: z.preprocess(
    (val) => (val === undefined ? false : val), 
    z.boolean().optional()
  ),
  specialistsInformation: z.array(z.object({ 
    name: zVal.textRequired(),
    type: zVal.textRequired(),
    phone: zVal.textRequired(),
    address: zVal.textRequired(),
  })),
  longTermCare: z.boolean(),
  facilityName: zVal.textOptionalEmpty(),
  facilityAddress: zVal.textOptionalEmpty(),
  facilityPhone: zVal.phoneOptionalEmpty(),
  otherInsuranceCoverage: z.boolean(),
  insuranceName: zVal.textOptionalEmpty(),
  insuranceId: zVal.textOptionalEmpty(),
  insuranceGroup: zVal.textOptionalEmpty(),
  otherPrescriptionDrugCoverage: z.boolean(),
  otherPrescriptionDrugCoverageName: zVal.textOptionalEmpty(),
  otherPrescriptionDrugCoverageMemberNumber: zVal.textOptionalEmpty(),
  otherPrescriptionDrugCoverageGroupNumber: zVal.textOptionalEmpty(),
  otherMedicalServicesCoverage: z.boolean(),
  otherMedicalServicesCoverageName: zVal.textOptionalEmpty(),
  otherMedicalServicesCoverageMemberNumber: zVal.textOptionalEmpty(),
}).superRefine((values, context) => {

  const requiredMessage = i18n.t('Required');

  if (values.hasMedicaid && !values.medicaidNumber) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["medicaidNumber"],
    });
  }

  if (values.PCPPatient === 'other' && !values.PCPPatientOther) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["PCPPatientOther"],
    });
  }

  if (values.hasChronicCondition) {
    const atLeastOneCardio = values.healthConditionsCardio.filter(item => item.checked);
    const atLeastOneDiabetes = values.healthConditionsDiabetes.filter(item => item.checked);
    if (atLeastOneCardio.length === 0 && atLeastOneDiabetes.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["healthConditionsCardio.0.checked"],
      });
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["healthConditionsDiabetes.0.checked"],
      });
    }
  }

  if (values.longTermCare && !values.facilityName) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["facilityName"],
    });
  }

  if (values.longTermCare && !values.facilityAddress) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["facilityAddress"],
    });
  }

  if (values.longTermCare && !values.facilityPhone) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["facilityPhone"],
    });
  }

  if (values.otherInsuranceCoverage && !values.insuranceName) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["insuranceName"],
    });
  }

  if (values.otherInsuranceCoverage && !values.insuranceId) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["insuranceId"],
    });
  }

  if (values.otherInsuranceCoverage && !values.insuranceGroup) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["insuranceGroup"],
    });
  }

  if (values.otherPrescriptionDrugCoverage && !values.otherPrescriptionDrugCoverageName) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["otherPrescriptionDrugCoverageName"],
    });
  }

  if (values.otherPrescriptionDrugCoverage && !values.otherPrescriptionDrugCoverageMemberNumber) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["otherPrescriptionDrugCoverageMemberNumber"],
    });
  }

  if (values.otherPrescriptionDrugCoverage && !values.otherPrescriptionDrugCoverageGroupNumber) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["otherPrescriptionDrugCoverageGroupNumber"],
    });
  }

  if (values.otherMedicalServicesCoverage && !values.otherMedicalServicesCoverageName) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["otherMedicalServicesCoverageName"],
    });
  }

  if (values.otherMedicalServicesCoverage && !values.otherMedicalServicesCoverageMemberNumber) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["otherMedicalServicesCoverageMemberNumber"],
    });
  }
  
  return z.NEVER; // The return value is not used, but we need to return something to satisfy the typing
});

function HealthDetails(props) {
  const { goToTab, updateData } = props;
  const methods = useForm({
    resolver: zodResolver(schema),
  });
  const {
    control,
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = methods;

  const onSubmit = (newData) => {

    const healthConditionsCardioValues = [];
    newData.healthConditionsCardio?.forEach((item, index) => {
      if (item.checked) {
        healthConditionsCardioValues.push({
          id: healthConditionsCardio[index]?.value,
        });
      }
    });

    const healthConditionsDiabetesValues = [];
    newData.healthConditionsDiabetes?.forEach((item, index) => {
      if (item.checked) {
        healthConditionsDiabetesValues.push({
          id: healthConditionsDiabetes[index]?.value,
        });
      }
    });

    updateData({
      ...newData,
      healthConditionsCardio: healthConditionsCardioValues,
      healthConditionsDiabetes: healthConditionsDiabetesValues,
    });

    goToTab('premium');
  };

  return (
    <main className="flex">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className="flex flex-col items-start self-center w-full max-w-full">
            <div className='w-full mb-10'>
              <MedicalHealthInformation />
            </div>
            <div className='w-full mb-10'>
              <PCPInformation />
            </div>
            <div className='w-full mb-10'>
              <HealthAssistance />
            </div>
            <div className='w-full mb-10'>
              <LongTermCare />
            </div>
            <div className='w-full mb-10'>
              <OtherInsuranceCoverageForm />
            </div>
          </div>
          <div className='w-full'>
            <div className="w-full flex flex-row justify-end gap-3 mt-5">
              <Button type='button' color='primary' variant='bordered' radius='full' onClick={() => goToTab('personal')}><Text>Previous</Text></Button>
              <Button type='submit' color='primary' radius='full'><Text>Next</Text></Button>
            </div>
          </div>     
        </form>
      </FormProvider>
    </main>
  );
}

export default HealthDetails;